import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import './WelcomePage.css';

import * as launchActions from '../actions/launch-actions';
import * as friendsActions from '../actions/friends-actions';
import * as roomsActions from '../actions/rooms-actions';
import * as inviteActions from '../actions/invite-actions';

import AppHero from '../components/AppHero';
import FriendList from '../components/FriendList';
import FilterableFriendList from '../components/FilterableFriendList';
import AddFriendForm from '../components/AddFriendForm';
import CreateRoomForm from '../components/CreateRoomForm';
import RoomInviteForm from '../components/RoomInviteForm.js';
import RoomList from '../components/RoomList';

/**
 * The main welcome page for the app. Handles a lot of UI state and data logic
 * for inviting users, adding friends, creating new rooms, etc.
 *
 * @class WelcomePage
 * @extends {Component}
 */

class WelcomePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      runningApp: false,
      creatingRoom: false,
      addingFriend: false,
      invitingToRoom: false
    };
  }

  fetchFriends = () => {
    console.log('Fetching friends...', this.props.friendsActions);
    this.props.friendsActions.requestFriendsList();
  }

  /**
   * Start the create room flow.
   *
   * @memberof WelcomePage
   */
  createRoom = () => {
    this.setState({ creatingRoom: true });
  }

  /**
   * Called when the user submits the form rendered by CreateRoomForm.
   * Takes in the desired name of the new room, as well as the list of user IDs
   * to invite to the room, then calls the appropriate Redux action.
   *
   * @memberof WelcomePage
   * @param {String} name The desired name of the room.
   * @param {String[]} members The list of user IDs to invite to the room.
   */
  onCreateRoom = ({ name, build, members }) => {
    console.log('Creating room with name:', name);
    console.log('Build:', build);
    console.log('And members:', members);

    this.props.roomsActions.createRoom({ name, build, members });
  }

  /**
   * Called when the user submits the form rendered by RoomInviteForm.
   * Takes in a list of user IDs to invite to the room, and calls the appropriate
   * Redux action.
   *
   * @memberof WelcomePage
   */
  onInviteUsersToRoom = (room, members) => {
    let memberIds = members.map((user) => user.id);
    this.props.roomsActions.inviteUsersToRoom(room, memberIds);
  }

  /**
   * Called when the user clicks "done" after creating a room. Switches the user out of
   * the room creation flow, and optionally calls the `joinRoom()` Redux action if the
   * passed `joinNewRoom` boolean is true.
   *
   * @memberof WelcomePage
   * @param {boolean} joinNewRoom Whether or not to join the newly created room.
   */
  finishCreatingRoom = (joinNewRoom) => {
    const { createdRoom } = this.props.roomsState;

    if (joinNewRoom) {
      this.props.roomsActions.joinRoom(createdRoom);
    }

    this.setState({ creatingRoom: false });
    this.props.roomsActions.clearCreatedRoom();
    this.props.roomsActions.requestRoomsList();
  }

  /**
   * Cancel the room creation flow. Called when the user clicks "cancel" while creating
   * a new room.
   *
   * @memberof WelcomePage
   */
  cancelCreateRoom = () => {
    this.setState({ creatingRoom: false });
  }

  /**
   * Start the add friend flow. Called when the user clicks "add friend" on the
   * main friends list UI.
   *
   * @memberof WelcomePage
   */
  addFriend = () => {
    this.setState({ addingFriend: true });
  }

  /**
   * Cancel the add friend flow. Called when the user clicks "cancel" while adding a new friend.
   *
   * @memberof WelcomePage
   */
  cancelAddFriend = () => {
    this.setState({ addingFriend: false });

    // Also clear out the "add friend" state.
    this.props.friendsActions.clearSearch();
  }

  /**
   * Open the invite flow for a given room ID.
   *
   * @memberof WelcomePage
   */
  inviteToRoom = (roomId) => {
    this.setState({ invitingToRoom: roomId });
  }

  /**
   * Cancel the room invite flow.
   *
   * @memberof WelcomePage
   */
  cancelInviteToRoom = () => {
    this.setState({ invitingToRoom: false });

    // Also clear out the "invite to rooms" state.
    this.props.roomsActions.clearInviteUsersToRoom();
  }

  /**
   * Render out the friends list section. This renders the full `FriendList` component, as well
   * as swaps it to render the `FilterableFriendsList` component when creating a new room.
   *
   * @memberof WelcomePage
   */
  renderFriendsList = () => {
    const { creatingRoom, invitingToRoom, addingFriend } = this.state;
    const { friends } = this.props.friends;

    let classNames = ['WelcomePage__section'];
    if (addingFriend) {
      classNames.push('disabled');
    };

    let header = (
      <header className="WelcomePage__section-header">
        <div className="WelcomePage__section-header-left">
          <h3 className="WelcomePage__header-text">Friends</h3>
        </div>
        <div className="WelcomePage__section-header-right">
          <a href="#" onClick={this.addFriend} className="WelcomePage__header-link"><svg width="11" height="11" viewBox="0 0 11 11" xmlns="http://www.w3.org/2000/svg"><path d="M5.04 10.08c.588 0 1.078-.49 1.078-1.078V6.09H9.03a1.04 1.04 0 0 0 1.05-1.05 1.04 1.04 0 0 0-1.05-1.05H6.118V1.078C6.118.49 5.628 0 5.04 0c-.588 0-1.078.49-1.078 1.078V3.99H1.05A1.04 1.04 0 0 0 0 5.04c0 .588.462 1.05 1.05 1.05h2.912v2.912c0 .588.49 1.078 1.078 1.078z" fill="#9C9C9C" fillRule="evenodd"/></svg> Add a friend</a>
        </div>
      </header>
    );

    let friendsList = (
      <FriendList className="WelcomePage__section-content" allowDragging={false} users={friends} />
    );

    // Drop the header and render a searchable friends list.
    if (creatingRoom || invitingToRoom) {
      header = null;
      friendsList = (
        <FilterableFriendList className="WelcomePage__section-content" allowDragging={true} users={friends} />
      );
    }

    return (
      <section className={classNames.join(' ')}>
        {header}
        {friendsList}
      </section>
    );
  }

  /**
   * Render the rooms list. This renders the full `RoomList` component, and disables it
   * with the `.disabled` class when the rooms UI should not be accessible (while adding
   * friends, for instance).
   *
   * @memberof WelcomePage
   */
  renderRoomsList = () => {
    const { addingFriend } = this.state;
    const { rooms } = this.props.roomsState;

    let classNames = ['WelcomePage__section'];
    if (addingFriend) {
      classNames.push('disabled');
    };

    return (
      <section className={classNames.join(' ')}>
        <header className="WelcomePage__section-header">
          <div className="WelcomePage__section-header-left">
            <h3 className="WelcomePage__header-text">Rooms</h3>
          </div>

          <div className="WelcomePage__section-header-right">
            <a href="#" onClick={this.createRoom} className="WelcomePage__header-link"><svg width="11" height="11" viewBox="0 0 11 11" xmlns="http://www.w3.org/2000/svg"><path d="M5.04 10.08c.588 0 1.078-.49 1.078-1.078V6.09H9.03a1.04 1.04 0 0 0 1.05-1.05 1.04 1.04 0 0 0-1.05-1.05H6.118V1.078C6.118.49 5.628 0 5.04 0c-.588 0-1.078.49-1.078 1.078V3.99H1.05A1.04 1.04 0 0 0 0 5.04c0 .588.462 1.05 1.05 1.05h2.912v2.912c0 .588.49 1.078 1.078 1.078z" fill="#9C9C9C" fillRule="evenodd"/></svg> Start a new room</a>
          </div>
        </header>

        <RoomList
          rooms={rooms}
          currentUser={this.props.session.user}
          className="WelcomePage__section-content"
          onRoomClick={this.props.roomsActions.joinRoom}
          openInviteFlow={this.inviteToRoom} />
      </section>
    );
  }

  /**
   * Render the room creation flow. Renders the full room creation section including the
   * CreateRoomForm component and section header.
   *
   * @memberof WelcomePage
   */
  renderCreateRoom = () => {
    const { createdRoom } = this.props.roomsState;
    let currentUser = this.props.session.user;

    return (
      <section className="WelcomePage__section">
        <header className="WelcomePage__section-header">
          <div className="WelcomePage__section-header-left">
            <h3 className="WelcomePage__header-text">Create a room</h3>
          </div>

          <div className="WelcomePage__section-header-right">
            <a href="#" onClick={this.cancelCreateRoom} className="WelcomePage__header-link">Cancel</a>
          </div>
        </header>

        <CreateRoomForm onSubmit={this.onCreateRoom}
          onFinish={this.finishCreatingRoom}
          createdRoom={createdRoom}
          currentUser={currentUser} />
      </section>
    )
  }

  /**
   * Render the room invite flow. Renders the full room invite section including the RoomInviteForm
   * component and section header.
   *
   * @memberof WelcomePage
   */
  renderInviteToRoom = () => {
    const { invitingToRoom } = this.state;

    const { rooms } = this.props.roomsState;
    let room = rooms.filter((r) => r.id === invitingToRoom)[0];

    return (
      <section className="WelcomePage__section">
        <header className="WelcomePage__section-header">
          <div className="WelcomePage__section-header-left">
            <h3 className="WelcomePage__header-text">Invite to {room.name}</h3>
          </div>

          <div className="WelcomePage__section-header-right">
            <a href="#" onClick={this.cancelInviteToRoom} className="WelcomePage__header-link">Cancel</a>
          </div>
        </header>

        <RoomInviteForm room={room}
          className="WelcomePage__section-content"
          invitedToRoom={this.props.roomsState.invitedToRoom}
          invitingToRoom={this.props.roomsState.invitingToRoom}
          errorInvitingToRoom={this.props.roomsState.errorInvitingToRoom}
          onSubmit={this.onInviteUsersToRoom}
          close={this.cancelInviteToRoom} />
      </section>
    );
  }

  /**
   * Render the add friend flow. Renders the full add friend flow including the `AddFriendForm`
   * and section header.
   *
   * @memberof WelcomePage
   */
  renderAddFriend = () => {
    return (
      <section className="WelcomePage__section">
        <header className="WelcomePage__section-header">
          <div className="WelcomePage__section-header-left">
            <h3 className="WelcomePage__header-text">Add a friend</h3>
          </div>

          <div className="WelcomePage__section-header-right">
            <a href="#" onClick={this.cancelAddFriend} className="WelcomePage__header-link">Close</a>
          </div>
        </header>

        <AddFriendForm
          className="WelcomePage__section-content"
          searchResults={this.props.friends.userSearchResult}
          hasSearched={this.props.friends.hasSearchedUsers}
          isSearching={this.props.friends.isSearchingUsers}
          errorSearching={this.props.friends.errorSearchingUsers}
          searchFriends={this.props.friendsActions.searchForUser}
          addFriend={this.props.friendsActions.createFriendRequest}
          inviteUser={this.props.inviteActions.createInvite} />
      </section>
    );
  }

  /**
   * The main render loop of the component.
   *
   * - Checks if we're currently adding a friend, and if so renders the add friend flow.
   *   Otherwise, it renders the frends list.
   * - Checks if we're currently creating a room, and if so renders the create room flow.
   *   Otherwise, it renders the rooms list.
   *
   * @returns
   * @memberof WelcomePage
   */
  render() {
    const { creatingRoom, invitingToRoom, addingFriend } = this.state;

    let friendsSection, roomsSection;
    if (addingFriend) {
      friendsSection = this.renderAddFriend();
    } else {
      friendsSection = this.renderFriendsList();
    }

    if (creatingRoom) {
      roomsSection = this.renderCreateRoom();
    } else if (invitingToRoom) {
      roomsSection = this.renderInviteToRoom();
    } else {
      roomsSection = this.renderRoomsList();
    }

    return (
      <div className="WelcomePage">
        <div className="WelcomePage__content">
          {friendsSection}
          {roomsSection}
        </div>
      </div>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    launch: state.launch,
    friends: state.friends,
    roomsState: state.rooms
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    friendsActions: bindActionCreators(friendsActions, dispatch),
    launchActions: bindActionCreators(launchActions, dispatch),
    roomsActions: bindActionCreators(roomsActions, dispatch),
    inviteActions: bindActionCreators(inviteActions, dispatch)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WelcomePage);



// WEBPACK FOOTER //
// ./src/containers/WelcomePage.js